import { useEffect } from 'react';
import { getDefaultAgentForNewChat } from '../../../utils/agentUtils.js';

/**
 * @param {object} p
 * @param {boolean} p.isFullscreen
 * @param {(next: 'split'|'fullscreen') => void} [p.onLayoutChange]
 * @param {string|null|undefined} p.selectedAgentId
 * @param {string|null|undefined} p.userId
 * @param {(agentId: string, userId: string) => void|Promise<void>} p.fetchConversations
 * @param {string|null|undefined} p.conversationId
 * @param {string|null|undefined} p.selectedConversationId
 * @param {(conversationId: string) => void|Promise<void>} p.fetchMessages
 * @param {boolean} p.isLoadingAgents
 * @param {object[]|null|undefined} p.agents
 * @param {object|null|undefined} p.defaultAgentForNewChat
 * @param {string|null|undefined} p.defaultAgentIdForNewChat
 * @param {(id: string, agent: object) => void|Promise<void>} p.selectAgent
 */
export function useAssistantSyncEffects({
  isFullscreen,
  onLayoutChange,
  selectedAgentId,
  userId,
  fetchConversations,
  conversationId,
  selectedConversationId,
  fetchMessages,
  isLoadingAgents,
  agents,
  defaultAgentForNewChat,
  defaultAgentIdForNewChat,
  selectAgent,
}) {
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onLayoutChange?.('split');
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [isFullscreen, onLayoutChange]);

  useEffect(() => {
    if (!selectedAgentId) return;
    void fetchConversations(selectedAgentId, userId ?? '');
  }, [selectedAgentId, userId, fetchConversations]);

  useEffect(() => {
    const cid = conversationId || selectedConversationId;
    if (!cid) return;
    void fetchMessages(cid);
  }, [conversationId, selectedConversationId, fetchMessages]);

  useEffect(() => {
    if (isLoadingAgents) return;
    if (selectedConversationId) return;
    if (selectedAgentId) return;
    const isCopilot = defaultAgentForNewChat?.isCopilot;
    const fallback = isCopilot && agents?.length ? getDefaultAgentForNewChat(agents) : null;
    const agentToSelect = fallback?.agent ?? defaultAgentForNewChat;
    const agentIdToSelect =
      fallback?.id ??
      (agentToSelect ? agentToSelect.uuid || agentToSelect.id : defaultAgentIdForNewChat);
    if (!agentIdToSelect || !agentToSelect) return;
    if (!isCopilot && !agents?.length) return;
    void selectAgent(agentIdToSelect, agentToSelect);
  }, [
    isLoadingAgents,
    agents,
    selectedAgentId,
    selectedConversationId,
    defaultAgentIdForNewChat,
    defaultAgentForNewChat,
    selectAgent,
  ]);
}
